import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePlanProyectos } from './usePlanProyectos'
import { PanelNuevoProyectoInline } from './PanelNuevoProyectoInline'
import { ModalMapaUbicacionProyecto } from './ModalMapaUbicacionProyecto'
import { TarjetaModuloEditable } from './TarjetaModuloEditable'
import { ComparadorPresupuesto } from './ComparadorPresupuesto'
import { ModalImportarArchivo } from './ModalImportarArchivo'
import { TarjetaNuevoModuloInline } from './TarjetaNuevoModuloInline'
import { PygProyectosPage } from './pyg/PygProyectosPage'
import { BancoApusPage } from './apus/BancoApusPage'
import { CurvaSProyectos } from './CurvaSProyectos'
import { PresupuestadorPage } from './presupuestos/PresupuestadorPage'
import { ModalActasCorte } from './cortes/ModalActasCorte'
import { useActasCorte } from './cortes/useActasCorte'
import { distribuirFechasEtapa } from './utilidadesFechas'
import {
  IconoEditar,
  IconoMas,
  IconoCheck,
  IconoArchivo,
  IconoFlechaArriba,
  IconoFlechaAbajo,
  IconoCerrar,
  IconoMapa,
  IconoGrafico,
  IconoCartera,
  IconoHerramienta,
} from '@/shared/ui/Iconos/Iconos'
import { CampoPesos } from './CampoPesos'
import { MarcaMega } from '@/shared/marca/MarcaMega'
import type { ProyectoPlan } from './tipos'
import estilos from './PlanProyectos.module.css'

function formatearMoneda(monto: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(monto)
}

function diasEntre(f1: string, f2: string) {
  const d1 = new Date(f1).getTime()
  const d2 = new Date(f2).getTime()
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24))
}

function formatearDiaMes(isoDate: string) {
  if (!isoDate) return ''
  const partes = isoDate.split('-')
  if (partes.length < 3) return isoDate
  const dia = parseInt(partes[2], 10)
  const mesIdx = parseInt(partes[1], 10) - 1
  const MESES_ABR = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
  return `${dia} ${MESES_ABR[mesIdx] || ''}`
}

export function PlanProyectosPage() {
  const {
    proyectos,
    crearProyecto,
    actualizarProyecto,
    actualizarModulo,
    eliminarModulo,
    actualizarEtapa,
    agregarEtapa,
    eliminarEtapa,
    agregarModulo,
    reordenarModulos,
    moverModulo,
    restaurarProyecto,
    moverModuloAEtapa,
    moverModulosLote,
    importarProyectoDesdeBorrador,
    asignarColaboradoresModulo,
    actualizarAvanceColaborador,
    reordenarProyectos,
  } = usePlanProyectos()

  const [elegidoId, setElegidoId] = useState<string>(() => proyectos[0]?.id || '')
  const [modalActasAbierto, setModalActasAbierto] = useState(false)
  const { actas: actasDelProyecto } = useActasCorte(elegidoId)
  const [searchParams, setSearchParams] = useSearchParams()
  type VistaPlan = 'gantt' | 'curvas' | 'pyg' | 'apus' | 'presupuestos'
  const vista = (searchParams.get('vista') as VistaPlan) || 'gantt'

  function cambiarVista(nuevaVista: VistaPlan) {
    const p = new URLSearchParams(searchParams)
    if (nuevaVista === 'gantt') {
      p.delete('vista')
    } else {
      p.set('vista', nuevaVista)
    }
    setSearchParams(p, { replace: true })
  }

  const [filtroEstadoProyecto, setFiltroEstadoProyecto] = useState<'todos' | 'en_curso' | 'finalizado' | 'licitacion'>('todos')
  const proyectosFiltrados = useMemo(() => {
    if (filtroEstadoProyecto === 'todos') return proyectos
    return proyectos.filter((p) => (p.estado_proyecto || 'en_curso') === filtroEstadoProyecto)
  }, [proyectos, filtroEstadoProyecto])

  const [abiertas, setAbiertas] = useState<Set<string>>(() => new Set())
  const [panelNuevoAbierto, setPanelNuevoAbierto] = useState(false)
  const [modalMapaAbierto, setModalMapaAbierto] = useState(false)
  const [modoEdicion, setModoEdicion] = useState(false)
  const [menuProyectosAbierto, setMenuProyectosAbierto] = useState(false)
  const [etapaCreandoModuloId, setEtapaCreandoModuloId] = useState<string | null>(null)

  // Cerrar etapas al cambiar de proyecto o pestaña
  useEffect(() => {
    setAbiertas(new Set())
  }, [elegidoId])


  // Estado para el Chip Flotante de Actualización Exitosa
  const [toastAvanceVisible, setToastAvanceVisible] = useState(false)
  const [modalImportarAbierto, setModalImportarAbierto] = useState(false)
  const [modulosSeleccionados, setModulosSeleccionados] = useState<Record<string, { etapaId: string }>>({})
  const timerToastRef = useRef<any>(null)

  function mostrarToastAvance() {
    if (timerToastRef.current) clearTimeout(timerToastRef.current)
    setToastAvanceVisible(true)
    timerToastRef.current = setTimeout(() => {
      setToastAvanceVisible(false)
    }, 2400)
  }


  // Snapshot para descartar cambios en modo edición
  const snapshotEdicionRef = useRef<ProyectoPlan | null>(null)

  // Estados de Drag & Drop
  const [dragEtapaId, setDragEtapaId] = useState<string | null>(null)
  const [dragOrigenIdx, setDragOrigenIdx] = useState<number | null>(null)
  const [dragDestinoIdx, setDragDestinoIdx] = useState<number | null>(null)

  const proyecto = useMemo(() => {
    return proyectos.find((p) => p.id === elegidoId) ?? proyectos[0]
  }, [proyectos, elegidoId])



  // Detectar si realmente se hicieron cambios durante el modo edición
  const hayCambios = useMemo(() => {
    if (!modoEdicion || !snapshotEdicionRef.current || !proyecto) return false
    return JSON.stringify(proyecto) !== JSON.stringify(snapshotEdicionRef.current)
  }, [modoEdicion, proyecto])

  const [etapaEditandoFocusId, setEtapaEditandoFocusId] = useState<string | null>(null)

  function asegurarModoEdicion() {
    if (!modoEdicion && proyecto) {
      snapshotEdicionRef.current = JSON.parse(JSON.stringify(proyecto))
      setModoEdicion(true)
    }
  }

  function handleCrearNuevaEtapa() {
    asegurarModoEdicion()
    const nueva = agregarEtapa(proyecto.id)
    if (nueva) {
      setAbiertas((prev) => new Set([...prev, nueva.id]))
      setEtapaEditandoFocusId(nueva.id)
      mostrarToastAvance()
    }
  }

  function alternarModoEdicion() {
    if (!modoEdicion) {
      snapshotEdicionRef.current = JSON.parse(JSON.stringify(proyecto))
      setModoEdicion(true)
    } else {
      snapshotEdicionRef.current = null
      setModoEdicion(false)
      setEtapaCreandoModuloId(null)
      setEtapaEditandoFocusId(null)
    }
  }

  function handleDescartarCambios() {
    if (snapshotEdicionRef.current) {
      restaurarProyecto(snapshotEdicionRef.current)
      snapshotEdicionRef.current = null
      setModoEdicion(false)
      setEtapaCreandoModuloId(null)
      setEtapaEditandoFocusId(null)
      mostrarToastAvance()
    }
  }

  // Cálculos dinámicos en tiempo real con sistema de coordenadas temporal unificado
  const calculos = useMemo(() => {
    if (!proyecto) return null
    const inicio = proyecto.fecha_inicio
    const fin = proyecto.fecha_fin
    const totalDiasProyecto = Math.max(1, diasEntre(inicio, fin))

    // 1. Horas totales y cálculo dinámico en cascada
    let totalHorasProyecto = 0
    let horasHechasProyecto = 0

    proyecto.etapas.forEach((e) => {
      e.modulos.forEach((m) => {
        totalHorasProyecto += m.horas
        horasHechasProyecto += m.horas * (m.avance / 100)
      })
    })

    const totalHorasSeguras = Math.max(1, totalHorasProyecto)
    let diasAcumulados = 0
    const hoyStr = new Date().toISOString().slice(0, 10)

    // 2. Etapas en cascada según carga horaria real sobre el plazo total
    const etapasDinamicas = proyecto.etapas.map((etapa) => {
      const sumHoras = Math.round(etapa.modulos.reduce((t, m) => t + m.horas, 0))
      const totalHorasRaw = etapa.modulos.reduce((t, m) => t + m.horas, 0)
      const sumHecho = etapa.modulos.reduce(
        (t, m) => t + m.horas * (Math.min(100, Math.max(0, m.avance)) / 100),
        0
      )

      // Comprobación exacta: si todos los módulos están al 100%, forzar 100%
      const todosModulos100 = etapa.modulos.length > 0 && etapa.modulos.every((m) => m.avance >= 100)
      let pctEtapa = 0
      if (todosModulos100) {
        pctEtapa = 1
      } else if (totalHorasRaw > 0) {
        pctEtapa = Math.min(0.99, Math.max(0, sumHecho / totalHorasRaw))
      }

      const pctNumero = todosModulos100 ? 100 : Math.round(pctEtapa * 100)

      // Proporción horaria y asignación de días
      const ratioHoras = sumHoras > 0 ? sumHoras / totalHorasSeguras : 1 / proyecto.etapas.length
      const diasEtapa = Math.max(1, Math.round(ratioHoras * totalDiasProyecto))

      // Fechas exactas en cascada
      const fInicioMs = new Date(inicio).getTime() + diasAcumulados * 86400000
      const fFinMs = fInicioMs + diasEtapa * 86400000
      const fInicio = new Date(fInicioMs).toISOString().slice(0, 10)
      const fFin = new Date(fFinMs).toISOString().slice(0, 10)

      const leftPct = (diasAcumulados / totalDiasProyecto) * 100
      const widthPct = Math.max(3.5, (diasEtapa / totalDiasProyecto) * 100)

      diasAcumulados += diasEtapa

      const esCompletada = todosModulos100 || pctEtapa >= 1
      const estadoDeducido =
        pctNumero === 0 ? 'por_empezar' : esCompletada ? 'hecha' : 'en_ejecucion'

      // Columna compacta Plazo & Avance (Verde garantizado si completada)
      let plazoTipo: 'completada' | 'vencida' | 'en_curso' | 'futura' = 'futura'
      let plazoEtiqueta = ''

      if (esCompletada) {
        plazoTipo = 'completada'
        plazoEtiqueta = 'Hecha'
      } else if (hoyStr > fFin) {
        plazoTipo = 'vencida'
        plazoEtiqueta = formatearDiaMes(fFin)
      } else if (hoyStr >= fInicio && hoyStr <= fFin) {
        plazoTipo = 'en_curso'
        plazoEtiqueta = formatearDiaMes(fFin)
      } else {
        plazoTipo = 'futura'
        plazoEtiqueta = formatearDiaMes(fInicio)
      }

      const plazoDetalle = `${pctNumero}%`

      return {
        ...etapa,
        sumHoras,
        pctEtapa: esCompletada ? 1 : pctEtapa,
        leftPct,
        widthPct,
        fInicio,
        fFin,
        diasEtapa,
        estadoDeducido,
        plazoInfo: {
          tipo: plazoTipo,
          etiqueta: plazoEtiqueta,
          detalle: plazoDetalle,
        },
      }
    })

    const avanceGlobal = totalHorasProyecto > 0 ? horasHechasProyecto / totalHorasProyecto : 0

    // 3. Posición exacta de HOY sobre el mismo sistema de coordenadas del proyecto
    const diasHoy = diasEntre(inicio, hoyStr)
    const hoyPct =
      diasHoy >= 0 && diasHoy <= totalDiasProyecto
        ? (diasHoy / totalDiasProyecto) * 100
        : null

    const transcurrido = Math.min(1, Math.max(0, diasHoy / totalDiasProyecto))

    // 4. Eje Temporal en Bloques de Meses: "1 ... 15 ... 31"
    const dInicioProj = new Date(inicio)
    const dFinProj = new Date(fin)

    // Agrupar meses que abarca el proyecto
    const mesesProyecto: {
      clave: string
      nombreMes: string
      diaInicio: number
      diaFin: number
      diasEnMes: number
      diasTotalesMes: number
      contieneHoy: boolean
      diaHoyNum: number
      anchoPct: number
    }[] = []

    let cursor = new Date(dInicioProj.getFullYear(), dInicioProj.getMonth(), 1)
    const finLimite = new Date(dFinProj.getFullYear(), dFinProj.getMonth() + 1, 0)

    const hoyDate = new Date()
    const hoyMesClave = `${hoyDate.getFullYear()}-${hoyDate.getMonth()}`
    const hoyDia = hoyDate.getDate()

    while (cursor <= finLimite) {
      const anio = cursor.getFullYear()
      const mes = cursor.getMonth()
      const clave = `${anio}-${mes}`
      const diasEnEsteMes = new Date(anio, mes + 1, 0).getDate()

      // Determinar día de inicio y día de fin dentro de este mes para el proyecto
      let dIni = 1
      if (anio === dInicioProj.getFullYear() && mes === dInicioProj.getMonth()) {
        dIni = dInicioProj.getDate()
      }

      let dFin = diasEnEsteMes
      if (anio === dFinProj.getFullYear() && mes === dFinProj.getMonth()) {
        dFin = dFinProj.getDate()
      }

      const diasEfectivos = Math.max(1, dFin - dIni + 1)
      const contieneHoy = clave === hoyMesClave && hoyDia >= dIni && hoyDia <= dFin

      const nombreMes = cursor
        .toLocaleString('es-CO', { month: 'long', year: 'numeric' })
        .toUpperCase()

      mesesProyecto.push({
        clave,
        nombreMes,
        diaInicio: dIni,
        diaFin: dFin,
        diasEnMes: diasEfectivos,
        diasTotalesMes: diasEnEsteMes,
        contieneHoy,
        diaHoyNum: hoyDia,
        anchoPct: (diasEfectivos / totalDiasProyecto) * 100,
      })

      // Siguiente mes
      cursor = new Date(anio, mes + 1, 1)
    }

    return {
      totalDias: totalDiasProyecto,
      totalHoras: Math.round(totalHorasProyecto),
      horasHechas: Math.round(horasHechasProyecto),
      avanceGlobal,
      transcurrido,
      etapasDinamicas,
      mesesProyecto,
      hoyPct,
    }
  }, [proyecto])

  function alternarEtapa(etapaId: string) {
    setAbiertas((prev) => {
      const next = new Set(prev)
      if (next.has(etapaId)) next.delete(etapaId)
      else next.add(etapaId)
      return next
    })
  }

  // Controladores Drag & Drop
  function handleDragStart(etapaId: string, idx: number) {
    setDragEtapaId(etapaId)
    setDragOrigenIdx(idx)
  }

  function handleDragOver(e: React.DragEvent, etapaId: string, idx: number) {
    e.preventDefault()
    if (dragEtapaId === etapaId && dragDestinoIdx !== idx) {
      setDragDestinoIdx(idx)
    }
  }

  function handleDragLeave() { }

  function handleDrop(etapaId: string, idx: number) {
    if (dragEtapaId === etapaId && dragOrigenIdx !== null && dragOrigenIdx !== idx) {
      reordenarModulos(proyecto.id, etapaId, dragOrigenIdx, idx)
    }
    setDragEtapaId(null)
    setDragOrigenIdx(null)
    setDragDestinoIdx(null)
  }

  function handleDragEnd() {
    setDragEtapaId(null)
    setDragOrigenIdx(null)
    setDragDestinoIdx(null)
  }

  if (!proyecto || !calculos) {
    return (
      <div className={estilos.contenedorVacio}>
        <p>No hay proyectos registrados en el plan.</p>
        <button
          type="button"
          className={estilos.btnNuevo}
          onClick={() => setPanelNuevoAbierto(true)}
        >
          + Crear primer proyecto
        </button>
      </div>
    )
  }

  return (
    <div className={estilos.pagina}>
      {/* Cabecera y Selector Multiproyecto */}
      <header className={estilos.topBarCard}>
        <div className={estilos.topBarInfo}>
          <div className={estilos.badgeSombra}>Modo Plan & Cronograma · Fuera de la Matriz</div>
          <h1 className={estilos.titulo}>Plan de Proyectos y Cronograma</h1>
        </div>

        <div className={estilos.topBarAcciones}>
          {/* Selector de proyectos con hamburguesa de alto contraste y reordenamiento libre */}
          <div className={estilos.selectorWrapperPersonalizado}>
            <label className={estilos.selectorLabel}>Proyecto:</label>
            <button
              type="button"
              className={estilos.btnSelectorProyecto}
              onClick={() => setMenuProyectosAbierto(!menuProyectosAbierto)}
              title="Abrir lista de proyectos y cambiar orden con flechas arriba/abajo"
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {proyecto.nombre}
              </span>
              <IconoFlechaAbajo />
            </button>

            {menuProyectosAbierto && (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 199 }}
                  onClick={() => setMenuProyectosAbierto(false)}
                />
                <div className={estilos.menuDesplegableProyectos}>
                  <div className={estilos.menuHeaderProyectos}>
                    <span>Banco de Proyectos</span>
                    <button
                      type="button"
                      onClick={() => setMenuProyectosAbierto(false)}
                      style={{ background: 'transparent', border: 0, color: '#ffc43f', cursor: 'pointer', fontWeight: 800, fontSize: '13px' }}
                      title="Cerrar menú"
                    >
                      <IconoCerrar />
                    </button>
                  </div>
                  {/* Filtro Banco de Proyectos: Todos, En Curso, Finalizados, Licitación */}
                  <div style={{ display: 'flex', gap: '4px', padding: '8px 12px', background: 'rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.1)', overflowX: 'auto' }}>
                    {(['todos', 'en_curso', 'finalizado', 'licitacion'] as const).map((est) => (
                      <button
                        key={est}
                        type="button"
                        onClick={() => setFiltroEstadoProyecto(est)}
                        style={{
                          background: filtroEstadoProyecto === est ? '#ffc43f' : 'transparent',
                          color: filtroEstadoProyecto === est ? '#1a0033' : '#ffffff',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {est === 'todos' ? `Todos (${proyectos.length})` : est === 'en_curso' ? 'En Curso' : est === 'finalizado' ? 'Finalizados' : 'Licitación'}
                      </button>
                    ))}
                  </div>
                  <div className={estilos.listaItemsProyectos}>
                    {proyectosFiltrados.map((p, idx) => {
                      const esActivo = p.id === proyecto.id
                      return (
                        <div
                          key={p.id}
                          className={`${estilos.itemProyectoFila} ${esActivo ? estilos.itemProyectoFilaActivo : ''}`}
                          onClick={() => {
                            setElegidoId(p.id)
                            setMenuProyectosAbierto(false)
                          }}
                        >
                          <span className={estilos.badgePosicionProyecto} title={`Posición #${idx + 1}`}>
                            #{idx + 1}
                          </span>
                          <div className={estilos.itemProyectoInfo}>
                            <span className={estilos.itemProyectoNombre}>{p.nombre}</span>
                            <span className={estilos.itemProyectoMeta}>
                              {p.codigo} · {p.etapas?.length || 0} fases
                            </span>
                          </div>
                          <div className={estilos.controlesItemOrden} onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={estilos.btnMoverProyecto}
                              disabled={idx === 0}
                              onClick={() => {
                                const copia = [...proyectos]
                                const [movido] = copia.splice(idx, 1)
                                copia.splice(idx - 1, 0, movido)
                                reordenarProyectos(copia)
                                mostrarToastAvance()
                              }}
                              title="Subir posición"
                            >
                              <IconoFlechaArriba />
                            </button>
                            <button
                              type="button"
                              className={estilos.btnMoverProyecto}
                              disabled={idx === proyectos.length - 1}
                              onClick={() => {
                                const copia = [...proyectos]
                                const [movido] = copia.splice(idx, 1)
                                copia.splice(idx + 1, 0, movido)
                                reordenarProyectos(copia)
                                mostrarToastAvance()
                              }}
                              title="Bajar posición"
                            >
                              <IconoFlechaAbajo />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Botón Descartar Cambios (Solo habilitado si hubo cambios reales) */}
          {modoEdicion && (
            <button
              type="button"
              className={estilos.btnDescartar}
              disabled={!hayCambios}
              onClick={handleDescartarCambios}
              title={
                hayCambios
                  ? 'Revertir los cambios realizados durante esta sesión'
                  : 'Sin modificaciones para descartar'
              }
            >
              Descartar cambios
            </button>
          )}

          {/* Botón para alternar Modo Edición en Vivo */}
          <button
            type="button"
            className={`${estilos.btnModoEdicion} ${modoEdicion ? estilos.modoEdicionActivo : ''}`}
            onClick={alternarModoEdicion}
            title="Activar o desactivar edición directa y reordenamiento"
          >
            <IconoEditar className={estilos.iconoBtn} />
            <span>{modoEdicion ? 'Finalizar Edición' : 'Modo Edición'}</span>
          </button>

          {/* Botón para abrir Actas de Corte y Cartera de Obra (Tareas 2.2, 2.8) */}
          <button
            type="button"
            className={estilos.btnModoEdicion}
            style={{ backgroundColor: '#f5f3ff', borderColor: '#c4b5fd', color: '#6d28d9' }}
            onClick={() => setModalActasAbierto(true)}
            title="Actas de corte de obra, sustento de pago y recaudo de cartera"
          >
            <IconoCartera className={estilos.iconoBtn} />
            <span>Actas de Corte {actasDelProyecto.length > 0 ? `(${actasDelProyecto.length})` : ''}</span>
          </button>

          {/* Botón para importar presupuesto PDF / TXT */}
          <button
            type="button"
            className={estilos.btnImportarPresupuesto}
            onClick={() => setModalImportarAbierto(true)}
            title="Cargar presupuesto PDF o TXT para procesar borrador en memoria"
          >
            <IconoArchivo className={estilos.iconoBtn} />
            <span>Importar Archivo / PDF</span>
          </button>

          {/* Botón para desplegar formulario inline de proyecto */}
          <button
            type="button"
            className={estilos.btnNuevo}
            onClick={() => setPanelNuevoAbierto(!panelNuevoAbierto)}
          >
            {panelNuevoAbierto ? 'Cerrar' : '+ Nuevo Proyecto'}
          </button>
        </div>
      </header>

      {/* Formulario Acordeón Inline de Nuevo Proyecto (CERO MODAL) */}
      <PanelNuevoProyectoInline
        abierto={panelNuevoAbierto}
        onCerrar={() => setPanelNuevoAbierto(false)}
        onCrear={(nuevo) => {
          const creado = crearProyecto(nuevo)
          setElegidoId(creado.id)
          setPanelNuevoAbierto(false)
        }}
      />

      {/* Pestañas de Navegación Operativa vs Curva S vs Financiera vs Presupuestos vs APUs */}
      <nav className={estilos.navTabsFinancieras} aria-label="Selector de vista de proyectos">
        <button
          type="button"
          className={`${estilos.tabFinanciera} ${vista === 'gantt' ? estilos.tabFinancieraActiva : ''}`}
          onClick={() => cambiarVista('gantt')}
        >
          <IconoGrafico style={{ width: '18px', height: '18px' }} />
          <span>Plan &amp; Gantt</span>
        </button>
        <button
          type="button"
          className={`${estilos.tabFinanciera} ${vista === 'curvas' ? estilos.tabFinancieraActiva : ''}`}
          onClick={() => cambiarVista('curvas')}
        >
          <IconoGrafico style={{ width: '18px', height: '18px' }} />
          <span>Curva S</span>
        </button>
        <button
          type="button"
          className={`${estilos.tabFinanciera} ${vista === 'pyg' ? estilos.tabFinancieraActiva : ''}`}
          onClick={() => cambiarVista('pyg')}
        >
          <IconoCartera style={{ width: '18px', height: '18px' }} />
          <span>P&amp;G Obra</span>
        </button>

        <div className={estilos.separadorTabs} aria-hidden="true" />

        <button
          type="button"
          className={`${estilos.tabFinanciera} ${vista === 'presupuestos' ? estilos.tabFinancieraActiva : ''}`}
          onClick={() => cambiarVista('presupuestos')}
        >
          <IconoArchivo style={{ width: '18px', height: '18px' }} />
          <span>Presupuestos</span>
        </button>
        <button
          type="button"
          className={`${estilos.tabFinanciera} ${vista === 'apus' ? estilos.tabFinancieraActiva : ''}`}
          onClick={() => cambiarVista('apus')}
        >
          <IconoHerramienta style={{ width: '18px', height: '18px' }} />
          <span>Banco APU</span>
        </button>
      </nav>

      {vista === 'presupuestos' ? (
        <PresupuestadorPage />
      ) : vista === 'apus' ? (
        <BancoApusPage proyectos={proyectos} />
      ) : vista === 'curvas' ? (
        <CurvaSProyectos
          proyecto={proyecto}
          onActualizarProyecto={(cambios) => {
            actualizarProyecto(proyecto.id, cambios)
            mostrarToastAvance()
          }}
        />
      ) : vista === 'pyg' ? (
        <PygProyectosPage proyectoIdInicial={proyecto.id} />
      ) : (
        <>
          {/* Tarjeta de Resumen y Métricas */}
          <section className={estilos.resumenCard}>
            <div className={estilos.resumenCol}>
              <span className={estilos.kpiEtiqueta}>Ubicación y Cliente</span>
              {modoEdicion ? (
                <>
                  <input
                    type="text"
                    value={proyecto.cliente || ''}
                    placeholder="Cliente o solicitante"
                    className={estilos.inputResumenInline}
                    title="Cliente o solicitante (editable)"
                    onChange={(e) => {
                      actualizarProyecto(proyecto.id, { cliente: e.target.value })
                      mostrarToastAvance()
                    }}
                  />
                  <input
                    type="text"
                    value={proyecto.ubicacion || ''}
                    placeholder="Ubicación o dirección"
                    className={estilos.inputResumenInline}
                    title="Ubicación de la obra (editable)"
                    onChange={(e) => {
                      actualizarProyecto(proyecto.id, { ubicacion: e.target.value })
                      mostrarToastAvance()
                    }}
                  />
                  <button
                    type="button"
                    className={estilos.btnSeleccionarMapa}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setModalMapaAbierto(true)
                    }}
                    title="Seleccionar coordenadas GPS y radio de geocelda en el mapa"
                  >
                    <IconoMapa style={{ width: '15px', height: '15px', display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }} />
                    <span>
                      {proyecto.latitud != null && proyecto.longitud != null
                        ? `GPS (${proyecto.radio_geocerca_m || 80}m) · Cambiar en mapa`
                        : 'Agregar ubicación / Seleccionar en mapa'}
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <b className={estilos.kpiValor}>{proyecto.cliente || 'Sin cliente asignado'}</b>
                  <span className={estilos.kpiSub}>
                    {proyecto.ubicacion || 'Sin ubicación'}
                    {proyecto.latitud != null && proyecto.longitud != null ? ` · GPS (${proyecto.radio_geocerca_m || 80}m)` : ''}
                  </span>
                </>
              )}
            </div>

            <div className={estilos.resumenCol}>
              <span className={estilos.kpiEtiqueta}>Presupuesto de Obra</span>
              {modoEdicion ? (
                <>
                  <CampoPesos
                    valor={proyecto.presupuesto || 0}
                    placeholder="Presupuesto en COP"
                    className={estilos.inputResumenInline}
                    title="Presupuesto total en COP (editable)"
                    onCambio={(v) => {
                      actualizarProyecto(proyecto.id, { presupuesto: v })
                      mostrarToastAvance()
                    }}
                  />
                  <span className={estilos.kpiSub}>
                    {proyecto.etapas.reduce((t, e) => t + e.modulos.length, 0)} módulos en {proyecto.etapas.length} etapas
                  </span>
                </>
              ) : (
                <>
                  <b className={estilos.kpiValor}>{formatearMoneda(proyecto.presupuesto)}</b>
                  <span className={estilos.kpiSub}>
                    {proyecto.etapas.reduce((t, e) => t + e.modulos.length, 0)} módulos en {proyecto.etapas.length} etapas
                  </span>
                </>
              )}
            </div>

            <div className={estilos.resumenCol}>
              <span className={estilos.kpiEtiqueta}>Plazo de Ejecución</span>
              {modoEdicion ? (
                <>
                  <div className={estilos.fechasRowInline}>
                    <input
                      type="date"
                      value={proyecto.fecha_inicio}
                      className={estilos.inputFechaInline}
                      title="Fecha inicio"
                      onChange={(e) => {
                        actualizarProyecto(proyecto.id, { fecha_inicio: e.target.value })
                        mostrarToastAvance()
                      }}
                    />
                    <span>al</span>
                    <input
                      type="date"
                      value={proyecto.fecha_fin}
                      className={estilos.inputFechaInline}
                      title="Fecha entrega"
                      onChange={(e) => {
                        actualizarProyecto(proyecto.id, { fecha_fin: e.target.value })
                        mostrarToastAvance()
                      }}
                    />
                  </div>
                  <span className={estilos.kpiSub}>
                    {calculos.totalDias} días · {calculos.totalHoras} h estimadas
                  </span>
                </>
              ) : (
                <>
                  <b className={estilos.kpiValor}>{calculos.totalDias} días</b>
                  <span className={estilos.kpiSub}>
                    {calculos.totalHoras} h estimadas · del {formatearDiaMes(proyecto.fecha_inicio)} al {formatearDiaMes(proyecto.fecha_fin)}
                  </span>
                </>
              )}
            </div>

            <div className={estilos.resumenColAvance}>
              <div className={estilos.avanceHeader}>
                <span className={estilos.kpiEtiqueta}>Avance Ponderado</span>
                <b>{Math.round(calculos.avanceGlobal * 100)}%</b>
              </div>
              <div className={estilos.barraProgreso}>
                <div
                  className={estilos.barraProgresoFill}
                  style={{ width: `${Math.round(calculos.avanceGlobal * 100)}%` }}
                />
                {calculos.hoyPct !== null && (
                  <div
                    className={estilos.marcaTiempoHoy}
                    style={{ left: `${calculos.hoyPct}%` }}
                    title="Posición de hoy"
                  />
                )}
              </div>
              <span className={estilos.kpiSubAtraso}>
                {calculos.avanceGlobal >= calculos.transcurrido
                  ? 'Proyecto al día con el cronograma'
                  : `Atraso temporal: requeriría ${Math.round(calculos.transcurrido * 100)}%`}
              </span>
            </div>
          </section>

          {/* Comparador Presupuestal y Financiero a 100% de Ancho (Estilo Arkano Hub) */}
          <ComparadorPresupuesto proyecto={proyecto} calculos={calculos} />

          {/* Lienzo Gantt con Columna Compacta y Eje "1 ... 15 ... 31" */}
          <section className={estilos.ganttCanvas}>
            {/* Cabecera del Gantt */}
            <div className={estilos.ganttHeaderLimpio}>
              <div className={estilos.colEtapaNombreHeader}>
                <span>Etapas y Capítulos</span>
                <button
                  type="button"
                  className={estilos.btnAgregarEtapaMini}
                  onClick={handleCrearNuevaEtapa}
                  title="Añadir nueva etapa al proyecto"
                >
                  <IconoMas className={estilos.iconoBtn} />
                  <span>Etapa</span>
                </button>
              </div>

              <div className={estilos.contenedorEjeTemporal}>
                {/* Fila 1: Nombres de Meses Proporcionales */}
                <div className={estilos.pistaMeses}>
                  {calculos.mesesProyecto.map((m) => (
                    <div
                      key={m.clave}
                      className={estilos.celdaMes}
                      style={{ width: `${m.anchoPct}%` }}
                    >
                      {m.nombreMes}
                    </div>
                  ))}
                </div>

                {/* Fila 2: Formato Minimalista "1 ... 15 ... 31" */}
                <div className={estilos.pistaPuntosDias}>
                  {calculos.mesesProyecto.map((m) => (
                    <div
                      key={m.clave}
                      className={estilos.bloqueMesPuntos}
                      style={{ width: `${m.anchoPct}%` }}
                    >
                      <span className={estilos.numDiaHito}>{m.diaInicio}</span>
                      <span className={estilos.puntosHito}>···</span>

                      {m.contieneHoy && (
                        <>
                          <span className={estilos.diaHoyPill}>{m.diaHoyNum}</span>
                          <span className={estilos.puntosHito}>···</span>
                        </>
                      )}

                      <span className={estilos.numDiaHito}>{m.diaFin}</span>
                    </div>
                  ))}

                  {/* Línea vertical roja limpia y 100% transparente en cabecera */}
                  {calculos.hoyPct !== null && (
                    <div
                      className={estilos.lineaHoyCabeceraLimpia}
                      style={{ left: `${calculos.hoyPct}%` }}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>

              {/* Columna Compacta Plazo & Avance (Mitad de ancho: 88px) */}
              <div className={estilos.colPlazoAvanceHeader}>Plazo</div>
            </div>

            {/* Filas de Etapas (Barras Dinámicas por Horas y Fechas en Cascada) */}
            <div className={estilos.ganttCuerpo}>
              {calculos.etapasDinamicas.map((etapa) => {
                const abierta = abiertas.has(etapa.id)

                return (
                  <div
                    key={etapa.id}
                    className={`${estilos.filaGrupo} ${abierta ? estilos.filaAbierta : ''}`}
                  >
                    <div className={estilos.filaEtapaLimpia}>
                      <div
                        className={estilos.colEtapaNombre}
                        onClick={() => alternarEtapa(etapa.id)}
                        role="button"
                        tabIndex={0}
                      >
                        <span className={estilos.flechaToggle}>
                          {abierta ? (
                            <IconoFlechaAbajo style={{ width: '12px', height: '12px', display: 'inline-block' }} />
                          ) : (
                            <IconoFlechaArriba style={{ width: '12px', height: '12px', display: 'inline-block', transform: 'rotate(90deg)' }} />
                          )}
                        </span>
                        <div className={estilos.titulosEtapa}>
                          {modoEdicion ? (
                            <input
                              ref={(el) => {
                                if (el && etapaEditandoFocusId === etapa.id) {
                                  el.focus()
                                  el.select()
                                }
                              }}
                              type="text"
                              value={etapa.nombre}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                actualizarEtapa(proyecto.id, etapa.id, { nombre: e.target.value })
                              }
                              onBlur={() => {
                                if (etapaEditandoFocusId === etapa.id) {
                                  setEtapaEditandoFocusId(null)
                                }
                              }}
                              className={estilos.inputEtapaNombreInline}
                            />
                          ) : (
                            <b
                              title="Doble clic para editar nombre de etapa"
                              onDoubleClick={(e) => {
                                e.stopPropagation()
                                asegurarModoEdicion()
                                setEtapaEditandoFocusId(etapa.id)
                              }}
                            >
                              {etapa.nombre}
                            </b>
                          )}
                          <span>
                            {etapa.modulos.length} módulos · {etapa.sumHoras} h est. · {etapa.diasEtapa} días
                          </span>
                        </div>
                      </div>

                      {/* Pista Gantt con la barra de etapa en cascada exacta */}
                      <div className={estilos.pistaGanttFila}>
                        {/* Barra de progreso DINÁMICA proporcional a las horas reales */}
                        <div
                          className={`${estilos.barraEtapa} ${estilos['estado_' + etapa.estadoDeducido]}`}
                          style={{ left: `${etapa.leftPct}%`, width: `${etapa.widthPct}%` }}
                          title={`${etapa.nombre}: del ${etapa.fInicio} al ${etapa.fFin} (${etapa.sumHoras} horas)`}
                        >
                          <div
                            className={estilos.barraEtapaProgreso}
                            style={{ width: `${Math.round(etapa.pctEtapa * 100)}%` }}
                          />
                        </div>

                        {/* Línea vertical continua HOY: cruza sobre la etapa correcta (Etapa 3 en Plaza de Mercado) */}
                        {calculos.hoyPct !== null && (
                          <div
                            className={estilos.lineaHoyFilaLimpia}
                            style={{ left: `${calculos.hoyPct}%` }}
                            aria-hidden="true"
                          />
                        )}
                      </div>

                      {/* Columna Compacta Plazo & Avance (88px) */}
                      <div className={estilos.colPlazoAvance}>
                        <div
                          className={`${estilos.badgePlazoCompacto} ${estilos['plazo_' + etapa.plazoInfo.tipo]}`}
                        >
                          <span className={estilos.plazoTextoCompacto}>{etapa.plazoInfo.etiqueta}</span>
                          <b className={estilos.plazoPctCompacto}>{etapa.plazoInfo.detalle}</b>
                        </div>
                      </div>
                    </div>

                    {/* Acordeón de Módulos (100% de Ancho) */}
                    {abierta && (
                      <div className={estilos.contenedorEtapaExpandida}>
                        {/* Barra de herramientas y notas de la etapa */}
                        <div className={estilos.etapaSubBar}>
                          <div className={estilos.etapaMetadatos}>
                            {modoEdicion ? (
                              <div className={estilos.etapaNotasEdicion}>
                                <input
                                  type="text"
                                  placeholder="Requisitos técnicos previos..."
                                  value={etapa.requerimientos || ''}
                                  onChange={(e) =>
                                    actualizarEtapa(proyecto.id, etapa.id, {
                                      requerimientos: e.target.value,
                                    })
                                  }
                                  className={estilos.inputNotaInline}
                                />
                                <input
                                  type="text"
                                  placeholder="Observaciones de la etapa..."
                                  value={etapa.observaciones || ''}
                                  onChange={(e) =>
                                    actualizarEtapa(proyecto.id, etapa.id, {
                                      observaciones: e.target.value,
                                    })
                                  }
                                  className={estilos.inputNotaInline}
                                />
                              </div>
                            ) : (
                              <>
                                {etapa.requerimientos && (
                                  <span className={estilos.etapaNotaItem}>
                                    <b>Requisitos:</b> {etapa.requerimientos}
                                  </span>
                                )}
                                {etapa.observaciones && (
                                  <span className={estilos.etapaNotaItem}>
                                    <b>Notas:</b> {etapa.observaciones}
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {modoEdicion && etapa.modulos.length === 0 && (
                              <button
                                type="button"
                                className={estilos.btnEliminarEtapa}
                                onClick={() => {
                                  if (window.confirm(`¿Eliminar la etapa "${etapa.nombre}"?`)) {
                                    eliminarEtapa(proyecto.id, etapa.id)
                                    mostrarToastAvance()
                                  }
                                }}
                                title="Eliminar esta etapa vacía"
                              >
                                <IconoCerrar className={estilos.iconoBtn} />
                                <span>Eliminar etapa</span>
                              </button>
                            )}
                            <button
                              type="button"
                              className={estilos.btnAgregarModulo}
                              onClick={() => {
                                asegurarModoEdicion()
                                setEtapaCreandoModuloId(
                                  etapaCreandoModuloId === etapa.id ? null : etapa.id
                                )
                              }}
                            >
                              <IconoMas className={estilos.iconoBtn} />
                              <span>
                                {etapaCreandoModuloId === etapa.id
                                  ? 'Cancelar'
                                  : 'Añadir módulo a esta etapa'}
                              </span>
                            </button>
                          </div>
                        </div>

                        {/* Grilla completa 100% con Drag & Drop y Edición Inline */}
                        <div className={estilos.grillaModulos}>
                          {(() => {
                            const etapaRangoReal = {
                              fecha_inicio: (etapa as any).fInicio || etapa.fecha_inicio,
                              fecha_fin: (etapa as any).fFin || etapa.fecha_fin,
                            }
                            const mapaFechas = distribuirFechasEtapa(etapa.modulos, etapaRangoReal)
                            return etapa.modulos.map((modulo, idx) => (
                              <TarjetaModuloEditable
                                key={modulo.id}
                                modulo={modulo}
                                indice={idx}
                                totalModulos={etapa.modulos.length}
                                modoEdicion={modoEdicion}
                                etapaFechaInicio={etapaRangoReal.fecha_inicio}
                                etapaFechaFin={etapaRangoReal.fecha_fin}
                                fechasCalculadasProp={mapaFechas.get(modulo.id)}
                                onActualizar={(cambios) => {
                                  actualizarModulo(proyecto.id, etapa.id, modulo.id, cambios)
                                  mostrarToastAvance()
                                }}
                                onEliminar={() =>
                                  eliminarModulo(proyecto.id, etapa.id, modulo.id)
                                }
                                onMover={(dir) =>
                                  moverModulo(proyecto.id, etapa.id, modulo.id, dir)
                                }
                                onDragStart={() => handleDragStart(etapa.id, idx)}
                                onDragOver={(e) => handleDragOver(e, etapa.id, idx)}
                                onDragLeave={handleDragLeave}
                                onDrop={() => handleDrop(etapa.id, idx)}
                                onDragEnd={handleDragEnd}
                                esArrastrando={dragEtapaId === etapa.id && dragOrigenIdx === idx}
                                esDestino={dragEtapaId === etapa.id && dragDestinoIdx === idx}
                                todasLasEtapas={proyecto.etapas}
                                etapaActualId={etapa.id}
                                onAsignarColaboradores={(colaboradoresIds) => {
                                  void asignarColaboradoresModulo(
                                    proyecto.id,
                                    etapa.id,
                                    modulo.id,
                                    colaboradoresIds
                                  )
                                  mostrarToastAvance()
                                }}
                                onActualizarAvanceColaborador={(colabId, nuevoAvance) => {
                                  void actualizarAvanceColaborador(
                                    proyecto.id,
                                    etapa.id,
                                    modulo.id,
                                    colabId,
                                    nuevoAvance
                                  )
                                  mostrarToastAvance()
                                }}
                                onMoverAEtapa={(etapaDestinoId) => {
                                  moverModuloAEtapa(proyecto.id, modulo.id, etapa.id, etapaDestinoId)
                                  mostrarToastAvance()
                                }}
                                seleccionado={Boolean(modulosSeleccionados[modulo.id])}
                                onAlternarSeleccion={() => {
                                  setModulosSeleccionados((prev) => {
                                    const copia = { ...prev }
                                    if (copia[modulo.id]) delete copia[modulo.id]
                                    else copia[modulo.id] = { etapaId: etapa.id }
                                    return copia
                                  })
                                }}
                              />
                            ))
                          })()}

                          {/* Tarjeta Borrador Inline para añadir módulo (CERO MODAL) */}
                          {etapaCreandoModuloId === etapa.id && (
                            <TarjetaNuevoModuloInline
                              codigoSugerido={`${etapa.orden}.${etapa.modulos.length + 1}`}
                              onGuardar={(nuevoMod) => {
                                agregarModulo(proyecto.id, etapa.id, nuevoMod)
                                setEtapaCreandoModuloId(null)
                              }}
                              onCancelar={() => setEtapaCreandoModuloId(null)}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Si no hay etapas en el proyecto */}
              {calculos.etapasDinamicas.length === 0 && (
                <div className={estilos.estadoVacioEtapas}>
                  <p>Este proyecto aún no tiene etapas definidas.</p>
                  <button
                    type="button"
                    className={estilos.btnAgregarEtapa}
                    onClick={handleCrearNuevaEtapa}
                  >
                    <IconoMas className={estilos.iconoBtn} />
                    <span>Crear Primera Etapa</span>
                  </button>
                </div>
              )}

              {/* Botón al final del Gantt para añadir nueva etapa */}
              {calculos.etapasDinamicas.length > 0 && (
                <div className={estilos.filaAccionNuevaEtapa}>
                  <button
                    type="button"
                    className={estilos.btnAgregarEtapa}
                    onClick={handleCrearNuevaEtapa}
                    title="Añadir una nueva etapa a este proyecto"
                  >
                    <IconoMas className={estilos.iconoBtn} />
                    <span>Añadir Etapa al Proyecto</span>
                  </button>
                </div>
              )}
            </div>
          </section>


          {/* Barra Flotante de Acciones en Lote */}
          {Object.keys(modulosSeleccionados).length > 0 && (
            <aside className={estilos.barraAccionesLote} role="toolbar" aria-label="Acciones en lote">
              <div className={estilos.loteInfo}>
                <span className={estilos.loteBadge}>
                  {Object.keys(modulosSeleccionados).length}
                </span>
                <span>módulos seleccionados</span>
              </div>

              <div className={estilos.loteAcciones}>
                <select
                  id="selectLoteDestino"
                  className={estilos.selectLoteDestino}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Mover a etapa destino...
                  </option>
                  {proyecto.etapas.map((et) => (
                    <option key={et.id} value={et.id}>
                      Etapa {et.orden}: {et.nombre}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className={estilos.btnLoteMover}
                  onClick={() => {
                    const sel = document.getElementById('selectLoteDestino') as HTMLSelectElement
                    const destinoId = sel?.value
                    if (!destinoId) return

                    const listaIds = Object.keys(modulosSeleccionados)
                    const porOrigen: Record<string, string[]> = {}
                    for (const id of listaIds) {
                      const orig = modulosSeleccionados[id].etapaId
                      if (!porOrigen[orig]) porOrigen[orig] = []
                      porOrigen[orig].push(id)
                    }

                    for (const [origenId, ids] of Object.entries(porOrigen)) {
                      moverModulosLote(proyecto.id, ids, origenId, destinoId)
                    }

                    setModulosSeleccionados({})
                    mostrarToastAvance()
                  }}
                >
                  Mover a esta etapa
                </button>

                <button
                  type="button"
                  className={estilos.btnLoteCancelar}
                  onClick={() => setModulosSeleccionados({})}
                >
                  Limpiar
                </button>
              </div>
            </aside>
          )}
        </>
      )}

      {/* Modal de Actas de Corte y Cartera de Obra (Tareas 2.2, 2.8) */}
      {modalActasAbierto && proyecto && (
        <ModalActasCorte
          abierto={modalActasAbierto}
          proyecto={proyecto}
          alCerrar={() => setModalActasAbierto(false)}
        />
      )}

      {/* Modal de Selección de Ubicación en Mapa */}
      {proyecto && (
        <ModalMapaUbicacionProyecto
          abierto={modalMapaAbierto}
          proyecto={proyecto}
          onCerrar={() => setModalMapaAbierto(false)}
          onGuardar={(lat, lng, radio, dir) => {
            const cambios: Partial<ProyectoPlan> = {
              latitud: lat,
              longitud: lng,
              radio_geocerca_m: radio,
            }
            if (dir && dir.trim()) {
              cambios.ubicacion = dir.trim()
            }
            actualizarProyecto(proyecto.id, cambios)
            mostrarToastAvance()
          }}
        />
      )}

      {/* Modal de Importación de Archivo / PDF */}
      <ModalImportarArchivo
        abierto={modalImportarAbierto}
        onCerrar={() => setModalImportarAbierto(false)}
        onConfirmar={(nuevoProy) => {
          importarProyectoDesdeBorrador(nuevoProy)
          setElegidoId(nuevoProy.id)
          mostrarToastAvance()
        }}
      />

      {/* Chip Flotante Estilo Marca Mega: Actualización Exitosa */}
      {toastAvanceVisible && (
        <aside className={estilos.chipToastFlotante} role="status" aria-live="polite">
          <div className={estilos.chipToastLogo}>
            <MarcaMega tamanoLogo={20} soloLogo={true} />
          </div>
          <div className={estilos.chipToastContenido}>
            <div className={estilos.chipToastTituloRow}>
              <IconoCheck className={estilos.chipIconoCheck} />
              <b>Actualización de avance exitosa</b>
            </div>
            <span>Progreso sincronizado en vivo</span>
          </div>
        </aside>
      )}
    </div>
  )
}
